import { cartEventPublisher, cartEventSubscriber } from "../../common/pubsub/cart-event-pub-sub.js";
import { CartItemsResultEventData, CartItemsAddedResultEventData, CartItemsRemovedResultEventData } from "../../common/pubsub/models/cart-items-result-event-data.js";
import { CartService } from "./cart-service.js";
import { IDetailedCartInfo } from "./interfaces/detailed-cart-info.js";
import { TempOrderLineProductContract } from "./interfaces/temp-order-line-product-contract.js";
import { Notify } from "../../../components-shared/utils/notify.js";
import { IDiscountCode } from "./interfaces/discount-code.js";
import { DiscountCouponsApi } from "./discount-coupons-api.js";
import { AppliedDiscountCodeInfoDtoUI, CouponDiscountsHelper } from "./coupon-discounts-helper.js";
import { ApplyCodeResult, ApplyCodesResult } from "./interfaces/apply-code-result.js";
import { Locale } from "../../common/interfaces/locate.js";
import { determineTranslation } from "../../../components-shared/utils/language.js";
import { FreeShippingApi } from "./free-shipping-api.js";
import { convertStringToNumber } from "../../utils/string.js";

/// TODO: Include orderDiscount price into SummaryCartInfoContract?
/// DiscountCoupons event to get discounts always should be called after getDetailedCartInfo to get totalDiscountSum.
/// totalDiscountSum from getDetailedCartInfo and DiscountApplicationResultDto are not the same at the momemnt of writing this code.

export class DetailedCartController {
    private static instance: DetailedCartController;

    constructor() {
        if(!window.cartSettings) throw Error("Cart settings are not defined");

        if (!DetailedCartController.instance) {
            DetailedCartController.instance = this;
            this._getData();
            this.initEventSubscriptions();
        }
        return DetailedCartController.instance;
    }

    /* #region Translations */
    private translations: ITranslationSet = {
        [Locale.En]:{
            couponNotFound: "Cupon not found",
            codeWasNotActivated: "Discount is not activated",
            alreadyApliedMessage: "code has already been applied",
            youHaveBetterDiscountCodeMessage: "The discount code is not activated, as the products in the shopping cart already have better discounts."
        },
        [Locale.No]: {
            couponNotFound: "Rabattkode ikke funnet",
            codeWasNotActivated: "Rabatt er ikke aktivert",
            alreadyApliedMessage: "koden er allerede brukt",
            youHaveBetterDiscountCodeMessage: "Rabattkoden er ikke aktivert, da produktene i handlekurven allerede har bedre rabatter."
        },
        [Locale.Dk]: {
            couponNotFound: "Rabatkode ikke fundet",
            codeWasNotActivated: "Rabatten er ikke aktiveret",
            alreadyApliedMessage: "Koden er allerede anvendt",
            youHaveBetterDiscountCodeMessage: "Rabatkoden er ikke aktiveret, da produkterne i indkøbskurven allerede har bedre rabatter."
        }
    };
    private labels = determineTranslation(this.translations) as IDetailedControllerLabels;
    /* #endregion */

    /* #region Public */
    public initEventSubscriptions(){
        cartEventSubscriber.onCartItemsAdded(this._onCartItemsAdded.bind(this));
        cartEventSubscriber.onCartItemsRemoved(this._onCartItemsRemoved.bind(this));
        cartEventSubscriber.onCartEmptied(this._onCartEmptied.bind(this));
        cartEventSubscriber.onCartQuantityUpdated(this._onCartQuantityUpdated.bind(this));
    }

    public getDetailedCartInfo(): IDetailedCartInfo{
        return this._detailedCartInfo;
    }

    public countSingles(line: TempOrderLineProductContract){
        return line.quantity % line.factor;
    }

    public countPackages(line: TempOrderLineProductContract){
        return Math.floor(line.quantity / line.factor);
    }

    public async removeLine(line: TempOrderLineProductContract){
        try{
            await this._cartService.removeFromCart(line.tempOrderLineID);
        } catch(error) {
            this._notify.success(`Remove orderline error`);
            console.error('Remove orderline error', error);
        }
    }

    public async clearCart(){
        try{
            await this._cartService.emptyCart();
        } catch(error) {
            this._notify.success(`Clear cart failed`);
            console.error('Clear cart failed', error);
        }
    }

    public async updateQuantity(quantity: number, line: TempOrderLineProductContract){
        line.quantity = quantity;
        this._updateCartLine(line);
    }

    public async onFactorInputChange(quantity: number, line: TempOrderLineProductContract){
        const oldPackageQuantity = this.countPackages(line);
        const newPackageQuantity = quantity;
        const packagesDifference = newPackageQuantity - oldPackageQuantity;
        line.quantity = Number(line.quantity + line.factor * packagesDifference);
        this._updateCartLine(line);
    }

    public async onFractionInputChange(quantity: number, line: TempOrderLineProductContract){
        const oldSinglesQuantity = this.countSingles(line);
        const newSinglesQuantity = quantity;
        const singlesDifference = newSinglesQuantity - oldSinglesQuantity;
        line.quantity = Number(line.quantity + singlesDifference);
        this._updateCartLine(line);
    }

    public async createOffer() { //Feature for admin users only
        await this._cartService.createOffer();
    }

    /* #region Discounts */
    public getSessionCouponDiscounts(): AppliedDiscountCodeInfoDtoUI[]{
        return this._couponDiscounts.sessionCouponDiscounts;
    }

    public selectDiscount(codeID: number): void {
        this._couponDiscounts.selectCouponDiscount(codeID);
        this._updateStates();
    }

    public unselectDiscount(codeID: number): void {
        this._couponDiscounts.unselectCouponDiscount(codeID);
        this._updateStates();
    }

    public getOrderDiscountVisibility(): boolean{
        const appliedDiscountInfo = this._couponDiscounts.getSelectedCombination();

        if(!appliedDiscountInfo) {
            return true;
        }

        if(appliedDiscountInfo.codesInfo.length === 0){ 
            return true; 
        }

        return appliedDiscountInfo.orderPrice.generalOrderDiscounts.length > 0;
    }
    /* #endregion */

    /* #endregion */

    /* #region Event handlers */
    private async _onCartQuantityUpdated(topic: string, eventData: CartItemsResultEventData){
        await this._updateDetailedCartInfo(eventData.result.detailedCartInfo);
        if (eventData.result.warnings?.length > 0) {
            const warnings = eventData.result.warnings.map(msg => msg.replace('\n', '<br />'));
            this._notify.error(warnings.join());
        }
    }

    private async _onCartItemsAdded(topic: string, eventData: CartItemsAddedResultEventData){
        await this._updateDetailedCartInfo(eventData.result.detailedCartInfo);
    }

    private async _onCartItemsRemoved(topic: string, eventData: CartItemsRemovedResultEventData){
        await this._updateDetailedCartInfo(eventData.result.detailedCartInfo);
    }

    private async _onCartEmptied(topic: string, eventData: CartItemsRemovedResultEventData){
        await this._updateDetailedCartInfo(eventData.result.detailedCartInfo);
    }
    /* #endregion */

    /* #region Private */
    private _inclVat: boolean = window.globalSettings.displayPricesIncludingVat;
    private _freeShipipngApi = new FreeShippingApi();
    private _cartService = new CartService();
    private _discountCouponsApi = new DiscountCouponsApi();
    private _appliedCodesName: string[] = [];
    private _applyCodesResult: ApplyCodesResult | undefined = undefined;
    private _detailedCartInfo: IDetailedCartInfo | undefined = undefined;
    private _couponDiscounts = new CouponDiscountsHelper();
    private _notify = new Notify();

    private _getData(): void{
        this._cartService.detailedCartInfo()
            .then((detailedCartInfo) => {
                this._appliedCodesName = detailedCartInfo.couponCodes?.map((discountCode: IDiscountCode) => discountCode.code) || [];
                this._updateDetailedCartInfo(detailedCartInfo);
            })
    }

    private async _updateCartLine(line: TempOrderLineProductContract){
        this._cartService.updateCartQuantity(line.tempOrderLineID, line.quantity, line.productID);
    }

    private async _updateDetailedCartInfo(data: IDetailedCartInfo) {
        this._detailedCartInfo = data;
        await this._restoreCodes();
    }

    /* #region Discount */
    private _publishDetailedCartChangedEvent() {
        cartEventPublisher.detailedCartChanged(this._detailedCartInfo);
    }

    private async _updatePrices() {
        const appliedDiscountInfo = this._couponDiscounts.getSelectedCombination();

        if (!appliedDiscountInfo) {
            return;
        }

        //Update order lines
        for (const orderLinePrice of appliedDiscountInfo.orderLinePrices) {
            this._detailedCartInfo.products.find(p => {
                if(p.tempOrderLineID === orderLinePrice.OrderLineID){
                    p.formattedPrice = orderLinePrice.UnitSellingPrice;
                    p.formattedLinePrice = orderLinePrice.TotalPrice;
                    p.formattedDiscount = orderLinePrice.TotalDiscountAmountStr;
                    p.linePrice = convertStringToNumber(orderLinePrice.TotalPrice); //is used in the order-line.ts condition private _showPricePerUnit: boolean = this.orderLine.linePrice > 0
                    p.lineDiscount = orderLinePrice.TotalDiscountAmount;
                    p.lineDiscountPercent = convertStringToNumber(orderLinePrice.DiscountPercentStr); //convertStringToNumber can be removed when we get propper price format from the server
                }
            });
        }

        //Update cart summary
        this._detailedCartInfo.summary.totalDiscount = appliedDiscountInfo.orderPrice.TotalDiscount_Value;
        this._detailedCartInfo.summary.formattedTotalDiscountInclVat = appliedDiscountInfo.orderPrice.TotalDiscount;
        this._detailedCartInfo.summary.formattedTotalVAT = appliedDiscountInfo.orderPrice.TotalVat;
        this._detailedCartInfo.summary.formattedTotalPrice = appliedDiscountInfo.orderPrice.TotalPriceInclVat;
        this._detailedCartInfo.summary.formattedTotalPriceExclVat = appliedDiscountInfo.orderPrice.TotalPriceExclVat;
        this._detailedCartInfo.summary.totalPrice = appliedDiscountInfo.orderPrice.TotalPriceInclVat_Value;
        this._detailedCartInfo.summary.totalPriceExclVat = appliedDiscountInfo.orderPrice.TotalPriceExclVat_Value;

        const orderSum = this._inclVat ? this._detailedCartInfo.summary.totalPrice : this._detailedCartInfo.summary.totalPriceExclVat
        this._detailedCartInfo.freeShippingInfo = await this._freeShipipngApi.getFreeShippingData(orderSum); //TODO: to use this record and avoid an extra request to FreeShippingApi - we need get propper prices from the server at freeshippinginfo contract (inclVat or/and exclVat).

        this._publishDetailedCartChangedEvent();
    }

    public async applyCode(code:string): Promise<ApplyCodeResult>{
        if (this._couponDiscounts.isCodeAlreadyEntered(code)) {
            this._notify.success(`"${code}" - ${this.labels.alreadyApliedMessage}`);
            return;
        }

        const enteredCodes = this._couponDiscounts.getEnteredCodeIds();
        const result = await this._discountCouponsApi.applyCode(code, enteredCodes);

        if (result.ErrorMessage) {
            this._notify.warning(`"${code}" - ${result.ErrorMessage}`);
            return;
        }

        this._recalculateDiscounts(result, code);

        return result;
    }

    private async _recalculateDiscounts(result: ApplyCodeResult, code: string) {
        this._couponDiscounts.combinations = result.DiscountInfo;

        let wasCouponActivated = false;
        (this._couponDiscounts.combinations).forEach((combination) => {
            if (this._couponDiscounts.isCodeIdInCombination(result.AppliedCodeID, combination)) {
                wasCouponActivated = true;
            }
        });

        if (!wasCouponActivated && !result.IsForDelivery) {
            this._notify.warning(this.labels.codeWasNotActivated);
            return;
        }

        const couponDiscount = {
            codeId: result.AppliedCodeID,
            code: code,
            isForDelivery: result.IsForDelivery,
            selected: true,
        } as AppliedDiscountCodeInfoDtoUI;

        this._couponDiscounts.addSessionCouponDiscount(couponDiscount);
        this._couponDiscounts.rebuildExclusionLists();
        this._couponDiscounts.updateCombinations(result.DiscountInfo);

        const bestCombination = this._couponDiscounts.getBestCombination();
        const isInBest = this._couponDiscounts.isCodeIdInCombination(couponDiscount.codeId, bestCombination);

        this._couponDiscounts.selectCombination(bestCombination);
        this._couponDiscounts.updateSessionCodesPrices();

        this._updateStates();

        if (!isInBest) {
            if (!couponDiscount.isForDelivery){
                this._notify.warning(this.labels.youHaveBetterDiscountCodeMessage);
            }
        }
    }

    private _saveCodes(): void{
        this._appliedCodesName = this._couponDiscounts.getSelectedCodes();
        this._discountCouponsApi.saveTempOrderCodes(this._appliedCodesName);
    }

    private async _updateStates(){
        this._updatePrices();
        this._saveCodes();
    }

    private async _restoreCodes(){
        this._applyCodesResult = await this._discountCouponsApi.applyCodes(this._appliedCodesName);

        if (!this._applyCodesResult.AppliedCodes && !this._applyCodesResult.AppliedCodes.length) {
            return;
        }

        this._couponDiscounts.restoreCodes(this._applyCodesResult);
        this._updatePrices();
    }
    /* #endregion */

    /* #endregion */
}

/* #region Local interfaces */
interface IDetailedControllerLabels{
    couponNotFound: string,
    alreadyApliedMessage: string,
    codeWasNotActivated: string,
    youHaveBetterDiscountCodeMessage: string
}

interface ITranslationSet {
    [key:string]: IDetailedControllerLabels
}
/* #endregion */